<template>
  <div class="flex h-screen bg-gray-50 font-sans text-gray-900">
    <!-- Sidebar -->
    <aside class="w-64 bg-white h-full flex flex-col border-r border-gray-100 fixed left-0 top-0 z-10 shadow-[4px_0_24px_rgba(0,0,0,0.02)]">
      <!-- Logo Area -->
      <div class="p-8 flex items-center space-x-3">
        <div class="h-10  flex items-center justify-center overflow-hidden">
           <img src="@/assets/logo.png" alt="Logo" class="w-full h-full object-cover" />
        </div>
        <div class="flex flex-col">
          <span class="text-[12px] text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full w-max mt-1">机构端</span>
        </div>
      </div>

      <!-- Navigation Menu -->
      <nav class="flex-1 px-4 space-y-6 mt-6 overflow-y-auto">
        <div v-for="(group, index) in visibleMenuGroups" :key="index">
          <div v-if="group.title" class="px-4 mb-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">
            {{ group.title }}
          </div>
          <div class="space-y-1">
            <router-link 
              v-for="item in group.items" 
              :key="item.path" 
              :to="item.path"
              class="flex items-center px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group"
              :class="[
                $route.path.startsWith(item.path) 
                  ? 'bg-emerald-50 text-emerald-600 font-bold shadow-sm' 
                  : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              ]"
            >
              <svg 
                class="w-5 h-5 mr-3 transition-colors"
                :class="[
                  $route.path.startsWith(item.path) ? 'text-emerald-600' : 'text-gray-400 group-hover:text-gray-600'
                ]"
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
                v-html="item.icon"
              ></svg>
              {{ item.name }}
            </router-link>
          </div>
        </div>
      </nav>

      <!-- Bottom User Profile -->
      <div class="p-6 mt-auto relative">
        <!-- Logout Popup -->
        <transition 
          enter-active-class="transition duration-200 ease-out"
          enter-from-class="transform scale-95 opacity-0 translate-y-2"
          enter-to-class="transform scale-100 opacity-100 translate-y-0"
          leave-active-class="transition duration-150 ease-in"
          leave-from-class="transform scale-100 opacity-100 translate-y-0"
          leave-to-class="transform scale-95 opacity-0 translate-y-2"
        >
          <div 
            v-if="showLogoutMenu" 
            class="absolute bottom-24 left-6 right-6 bg-white shadow-[0_4px_20px_rgba(0,0,0,0.08)] border border-gray-100 rounded-xl py-2 z-50 overflow-hidden"
          >
             <button 
              @click="handleLogout"
              class="w-full py-2.5 text-center text-red-500 font-bold tracking-wide hover:bg-red-50 transition-colors"
            >
              登出
            </button>
          </div>
        </transition>

        <!-- Invisible backdrop to close menu -->
        <div v-if="showLogoutMenu" class="fixed inset-0 z-40" @click="showLogoutMenu = false"></div>

        <div 
          @click="showLogoutMenu = !showLogoutMenu"
          class="bg-white border border-gray-100 shadow-sm rounded-xl p-3 flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer relative z-50"
        >
          <div class="flex items-center space-x-3 overflow-hidden">
             <!-- Avatar -->
            <div class="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 font-bold text-sm shrink-0">
              {{ displayUserInitial }}
            </div>
            <div class="flex flex-col min-w-0">
              <p class="text-[10px] text-gray-400 leading-tight">当前用户</p>
              <p class="font-bold text-gray-800 text-sm truncate leading-tight">{{ displayUserName }}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="flex-1 ml-64 p-8 overflow-y-auto">
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Logout Confirmation Modal -->
    <div v-if="showLogoutConfirm" class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div class="bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 transform transition-all scale-100">
        <h3 class="text-xl font-bold text-gray-800 mb-4">确认退出</h3>
        <p class="text-gray-600 mb-6">确定要退出登录吗？</p>
        
        <div class="flex justify-end space-x-3">
          <button @click="showLogoutConfirm = false" class="px-4 py-2 text-gray-600 hover:bg-gray-50 rounded-lg transition">取消</button>
          <button @click="confirmLogout" class="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-sm">
            确认退出
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { httpRequest } from '@/utils/request'

export default {
  name: 'MainLayout',
  data() {
    return {
      showLogoutMenu: false,
      showLogoutConfirm: false,
      menuGroups: [
        {
          title: '问诊服务',
          items: [
            { 
              name: '开放问诊', 
              path: '/doctor/start',
              icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />'
            },
            { 
              name: '专科问诊', 
              path: '/doctor/specialized',
              icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />'
            }
          ]
        },
        {
          title: '数据管理',
          items: [
            { 
              name: '开放问诊记录', 
              path: '/doctor/open-records',
              icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />'
            },
            { 
              name: '专科问诊记录', 
              path: '/doctor/records',
              icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />'
            },
            { 
              name: '用户管理', 
              path: '/doctor/patients',
              icon: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />'
            }
          ]
        }
      ],
      currentUser: {
        name: '',
        avatar: '',
        can_use_special_diagnosis: true,
        can_use_open_diagnosis: true,
        can_view_patient_diagnosis_history: true
      }
    }
  },
  computed: {
    displayUserName() {
      if (this.currentUser && this.currentUser.name) {
        return this.currentUser.name
      }
      return '未登录'
    },
    displayUserInitial() {
      const name = this.currentUser && this.currentUser.name ? this.currentUser.name : ''
      if (name) {
        return name.charAt(0)
      }
      return '用'
    },
    visibleMenuGroups() {
      const can_use_special_diagnosis = this.currentUser && typeof this.currentUser.can_use_special_diagnosis === 'boolean'
        ? this.currentUser.can_use_special_diagnosis
        : true
      const can_use_open_diagnosis = this.currentUser && typeof this.currentUser.can_use_open_diagnosis === 'boolean'
        ? this.currentUser.can_use_open_diagnosis
        : true

      const groups = Array.isArray(this.menuGroups) ? this.menuGroups : []
      const mapped = groups.map((group) => {
        const items = Array.isArray(group.items) ? group.items : []
        const filtered = items.filter((item) => {
          if (!item || !item.path) return false
          if (item.path === '/doctor/start') return can_use_open_diagnosis
          if (item.path === '/doctor/open-records') return can_use_open_diagnosis
          if (item.path === '/doctor/specialized') return can_use_special_diagnosis
          if (item.path === '/doctor/records') return can_use_special_diagnosis
          return true
        })
        return { ...group, items: filtered }
      })
      return mapped.filter((g) => Array.isArray(g.items) && g.items.length)
    }
  },
  mounted() {
    this.loadCurrentUser()
  },
  methods: {
    handleLogout() {
      this.showLogoutMenu = false;
      this.showLogoutConfirm = true;
    },
    confirmLogout() {
      this.showLogoutConfirm = false;
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          window.localStorage.removeItem('token')
          window.localStorage.removeItem('user_profile')
        }
      } catch (e) {
        console.error(e)
      }
      this.currentUser = {
        name: '',
        avatar: '',
        can_use_special_diagnosis: true,
        can_use_open_diagnosis: true,
        can_view_patient_diagnosis_history: true
      }
      this.$router.push('/login');
    },
    loadCurrentUser() {
      let cachedProfile = null
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const cachedString = window.localStorage.getItem('user_profile')
          if (cachedString) {
            cachedProfile = JSON.parse(cachedString)
          }
        }
      } catch (e) {
        cachedProfile = null
      }
      if (cachedProfile) {
        this.currentUser = {
          name: cachedProfile.name || '',
          avatar: cachedProfile.avatar || '',
          can_use_special_diagnosis: typeof cachedProfile.can_use_special_diagnosis === 'boolean' ? cachedProfile.can_use_special_diagnosis : true,
          can_use_open_diagnosis: typeof cachedProfile.can_use_open_diagnosis === 'boolean' ? cachedProfile.can_use_open_diagnosis : true,
          can_view_patient_diagnosis_history: typeof cachedProfile.can_view_patient_diagnosis_history === 'boolean' ? cachedProfile.can_view_patient_diagnosis_history : true
        }
      }
      this.fetchUserProfileFromServer()
    },
    async fetchUserProfileFromServer() {
      let storedToken = null
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          storedToken = window.localStorage.getItem('token')
        }
      } catch (e) {
        storedToken = null
      }
      if (!storedToken) {
        return
      }
      try {
        const response = await httpRequest('get', '/v1/user/profile', {
          headers: {
            'Client-Type': '1'
          }
        })
        const data = response && response.data ? response.data : null
        if (data) {
          const userInfo = {
            name: data.name || '',
            avatar: data.avatar || '',
            can_use_special_diagnosis: this.currentUser && typeof this.currentUser.can_use_special_diagnosis === 'boolean'
              ? this.currentUser.can_use_special_diagnosis
              : true,
            can_use_open_diagnosis: this.currentUser && typeof this.currentUser.can_use_open_diagnosis === 'boolean'
              ? this.currentUser.can_use_open_diagnosis
              : true,
            can_view_patient_diagnosis_history: this.currentUser && typeof this.currentUser.can_view_patient_diagnosis_history === 'boolean'
              ? this.currentUser.can_view_patient_diagnosis_history
              : true
          }
          this.currentUser = userInfo
          try {
            if (typeof window !== 'undefined' && window.localStorage) {
              window.localStorage.setItem('user_profile', JSON.stringify(userInfo))
            }
          } catch (e) {
            console.error(e)
          }
        }
      } catch (error) {
        console.error(error)
      }
    }
  }
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
